"use client";

import {OneToastContainer} from "@/components/commons/one-toast-container";
import {Sparkles, ArrowRight} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import {useUser} from "@/components/commons/user-context";
import {SHOWCASE_APPS} from "@/lib/uiConstants";
import PromptInput from "@/components/dashboard/prompt";
import {paths} from "@/lib/paths";

export default function Home() {
  const {user, hydrated, logout} = useUser();

  return (
    <div className="min-h-screen bg-white">
      <OneToastContainer />
      {/* Top Navigation */}
      <div className="absolute top-2 left-6 right-6 flex items-center justify-between z-10">
        {/* Logo - Top Left Corner */}
        <Link href="/" className="flex items-center">
          <Image src="/rtzen-logo.svg" alt="rtZen Logo" width={79} height={42} priority />
        </Link>

        {/* Navigation Links - Top Right Corner */}
        <div className="flex items-center space-x-4">
          {hydrated && user && (
            <Link href={paths.dashboard.preview.root} className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium">
              Dashboard
            </Link>
          )}
          {hydrated && user ? (
            <button onClick={logout} className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium">
              Logout
            </button>
          ) : (
            <Link href="/login" className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium">
              Login
            </Link>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <div className="flex flex-col items-center justify-center px-4 pt-40 pb-16">
        <PromptInput currentMode="Preview" />
      </div>

      {/* Showcase Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="relative">
                <Sparkles className="w-7 h-7 text-purple-600 animate-spin-slow" />
                <div className="absolute inset-0 blur-lg bg-gradient-to-r from-purple-400/40 to-blue-400/40 animate-pulse" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Featured Apps</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Explore finance apps built with AI on rtZen.dev</p>
          </div>

          {/* Apps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {SHOWCASE_APPS.map(app => (
              <a
                key={app.id}
                href={`https://${app.slug}.rtzen.dev`}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-white rounded-2xl overflow-hidden border-2 border-gray-200/60 hover:border-blue-400/60 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer block">
                {/* Gradient border glow effect on hover */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-blue-500/0 group-hover:from-blue-500/20 group-hover:via-purple-500/20 group-hover:to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                {/* Image Container */}
                <div className="relative h-64 bg-gradient-to-br from-gray-100 to-gray-50 overflow-hidden border-b border-gray-100">
                  <Image
                    src={app.image}
                    alt={app.title}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                  {/* Subtle overlay for better text contrast */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                {/* Content */}
                <div className="relative p-6 bg-white">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{app.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-2 h-10">{app.description}</p>
                  <div className="flex items-center text-blue-600 font-medium text-sm group-hover:translate-x-1 transition-transform">
                    <span>View App</span>
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* View All Button */}
          <div className="text-center">
            <Link
              href="/showcase"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl">
              View All Apps
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-2">
              {/* <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">R</span>
              </div> */}
              <span className="text-xl font-semibold text-gray-900">rtZen.dev</span>
            </div>

            {/* Navigation Links */}
            <div className="flex items-center space-x-8">
              <Link href="/" className="text-gray-600 hover:text-gray-900 transition-colors text-sm">
                Home
              </Link>
              <a href="https://www.rtzen.ai/about" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900 transition-colors text-sm">
                About
              </a>
              <a href="https://www.rtzen.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900 transition-colors text-sm">
                Terms
              </a>
              <a href="https://www.rtzen.ai/privacy" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900 transition-colors text-sm">
                Privacy
              </a>
              <a
                href="https://www.linkedin.com/company/rtzen/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit rtZen LinkedIn page"
                className="text-gray-600 hover:text-gray-900 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-8 border-t border-gray-100">
            <p className="text-center text-sm text-gray-500">© {new Date().getFullYear()} rtZen.dev | v2025.12.09.21.29 </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
